﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

import * as accountModel from "~base/Services/API/Account/Model";
import { v4 as uuid } from "uuid";

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

//-------------------------------------------------------------------------------------
// State
//-------------------------------------------------------------------------------------

type SetLoading = { type: "setLoading" };
type InitDataLoadedState = {
    type: "initDataLoaded";
    data: accountModel.GetRegisterResponse;
    defaultLanguage: string;
};
type Save = { type: "save" };
type SetField = { type: "setField"; name: string; value: string | boolean };
type ChangeRådgivarKod = {
    type: "changeRådgivarKod";
    code: string;
    id: string;
    isChosen: boolean;
};
type AddRådgivarKod = { type: "addRådgivarKod" };
type RemoveRådgivarKod = {
    type: "removeRådgivarKod";
    code: string;
    id: string;
};
type SetFetchError = { type: "fetchError"; message: string };
type Navigate = { type: "navigate"; uri: string };

export type RegisterPageViewAction =
    | SetLoading
    | InitDataLoadedState
    | SetField
    | ChangeRådgivarKod
    | AddRådgivarKod
    | RemoveRådgivarKod
    | SetFetchError
    | Save
    | Navigate;

export interface RegisterPageState {
    registerInfo?: accountModel.GetRegisterResponse;
    postData?: accountModel.PostRegisterRequest;
    phone?: string;
    email?: string;
    uiCulture?: string;

    arbetarMedDistansrådgivning?: boolean;
    verksamhetsort?: string;
    rådgivarkod?: string;
    rådgivarKoder?: RådgivarKod[];

    errorMessage?: string;
    loading: boolean;
    navigateUri?: string;
}

export interface RådgivarKod {
    rådgivarKod: string;
    id: string;
}

export function reducer<T>(
    state: RegisterPageState,
    action: RegisterPageViewAction
): RegisterPageState {
    switch (action.type) {
        case "setLoading": {
            return {
                ...state,
                errorMessage: null,
                loading: true,
            };
        }
        case "initDataLoaded": {
            const koder: RådgivarKod[] =
                action.data.RådgivarKoder?.length > 0
                    ? action.data.RådgivarKoder.map((x) => {
                          return <RådgivarKod>{ rådgivarKod: x, id: uuid() };
                      })
                    : [{ rådgivarKod: "", id: uuid() }];

            return {
                ...state,
                registerInfo: action.data,
                arbetarMedDistansrådgivning:
                    action.data.JobbarMedDistansRådgivning,
                email: action.data.Email,
                phone: action.data.Phone,
                rådgivarkod:
                    action.data.RådgivarKodDefault ??
                    action.data.RådgivarKod ??
                    koder[0].rådgivarKod,
                rådgivarKoder: koder,
                verksamhetsort: action.data.Verksamhetsort,
                uiCulture:
                    action.data.PreferredUICulture ?? action.defaultLanguage,
                loading: false,
                errorMessage: null,
            };
        }
        case "setField": {
            const newState = { ...state };
            newState[action.name] = action.value;
            return newState;
        }
        case "changeRådgivarKod": {
            const newRådgivarKoder = [...state.rådgivarKoder];
            const kodToChange = newRådgivarKoder.find(
                (x) => x.id === action.id
            );
            kodToChange.rådgivarKod = action.code;
            return {
                ...state,
                rådgivarKoder: newRådgivarKoder,
                rådgivarkod: action.isChosen ? action.code : state.rådgivarkod,
            };
        }
        case "addRådgivarKod": {
            const kodToAdd: RådgivarKod = {
                rådgivarKod: "",
                id: uuid(),
            };
            const newRådgivarKoder = [...state.rådgivarKoder, kodToAdd];
            return {
                ...state,
                rådgivarKoder: newRådgivarKoder,
            };
        }
        case "removeRådgivarKod": {
            const newRådgivarKoder: RådgivarKod[] = state.rådgivarKoder.filter(
                (x) => x.id != action.id
            );
            const potentialNewKod =
                action.code === state.rådgivarkod
                    ? newRådgivarKoder[0].rådgivarKod
                    : state.rådgivarkod;
            return {
                ...state,
                rådgivarKoder: newRådgivarKoder,
                rådgivarkod: potentialNewKod,
            };
        }
        case "fetchError": {
            return {
                ...state,
                errorMessage: action.message,
            };
        }
        case "save": {
            return {
                ...state,
                errorMessage: null,
                postData: {
                    Email: state.email,
                    JobbarMedDistansRådgivning:
                        state.arbetarMedDistansrådgivning,
                    Phone: state.phone,
                    PreferredUICulture: null,
                    RådgivarKod: state.rådgivarkod,
                    Verksamhetsort: state.verksamhetsort,
                },
            };
        }
        case "navigate": {
            return {
                ...state,
                errorMessage: null,
                postData: null,
                loading: true,
                navigateUri: action.uri,
            };
        }
    }
}
